import { getStorage, ref, uploadString } from "firebase/storage";
import { writeBatch, doc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

const storage = getStorage();

export function getProjectStorageBasePath(websiteId) {
  return `kairos-clients-projects/${websiteId}`;
}

export function getProjectFilePath(websiteId, relativePath) {
  return `${getProjectStorageBasePath(websiteId)}/${relativePath}`;
}

function getFileName(relativePath) {
  const parts = relativePath.split("/");
  return parts[parts.length - 1] || "";
}

function getDirectory(relativePath) {
  const parts = relativePath.split("/");
  parts.pop();
  return parts.join("/") || "";
}

function getExtension(fileName) {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

function getLanguageFromExtension(extension) {
  if (extension === "js" || extension === "jsx") return "javascript";
  if (extension === "css") return "css";
  if (extension === "json") return "json";
  return "text";
}

function getFileRole(relativePath) {
  if (relativePath === "src/main.js" || relativePath === "src/main.jsx") return "entry";
  if (relativePath === "src/App.js" || relativePath === "src/App.jsx") return "app";
  if (relativePath.includes("/pages/")) return "page";
  if (relativePath.includes("/components/")) {
    if (relativePath.endsWith("_keep")) return "placeholder";
    return "component";
  }
  if (relativePath.includes("/helpers/")) return "helper";
  if (relativePath.includes("/styles/")) return "style";
  if (relativePath.includes("/firebase/")) return "config";
  return "unknown";
}

function isEditableFile(relativePath) {
  return !relativePath.endsWith("_keep");
}

function buildFileManifest(file) {
  const name = getFileName(file.relativePath);
  const directory = getDirectory(file.relativePath);
  const extension = getExtension(name);
  const language = getLanguageFromExtension(extension);
  const fileRole = getFileRole(file.relativePath);

  return {
    name,
    relativePath: file.relativePath,
    fullPath: file.fullPath,
    directory,
    extension,
    language,
    fileRole,
    editable: isEditableFile(file.relativePath),
    contentType: file.contentType,
    sizeEstimate: file.content.length,
    status: "active",
  };
}

function createEmptyRootFolderDoc(rootFolder, websiteId) {
  return {
    name: rootFolder,
    type: "root-folder",
    relativePath: rootFolder,
    storageBasePath: `${getProjectStorageBasePath(websiteId)}/${rootFolder}`,
    folders: {},
    files: {},
  };
}

function addFileToTree(rootDoc, manifest) {
  const relativeParts = manifest.relativePath.split("/");
  const rootFolder = relativeParts[0];
  const fileName = relativeParts[relativeParts.length - 1];
  const folderParts = relativeParts.slice(1, -1);

  if (rootDoc.name !== rootFolder) return rootDoc;

  let currentNode = rootDoc;

  for (const folderName of folderParts) {
    if (!currentNode.folders[folderName]) {
      const parentPath = currentNode.relativePath;
      const currentPath = `${parentPath}/${folderName}`;

      currentNode.folders[folderName] = {
        name: folderName,
        type: "folder",
        relativePath: currentPath,
        folders: {},
        files: {},
      };
    }

    currentNode = currentNode.folders[folderName];
  }

  currentNode.files[fileName] = manifest;

  return rootDoc;
}

function buildRootFolderDocuments(files, websiteId) {
  const rootDocs = {
    src: createEmptyRootFolderDoc("src", websiteId),
    assets: createEmptyRootFolderDoc("assets", websiteId),
    public: createEmptyRootFolderDoc("public", websiteId),
  };

  for (const file of files) {
    const manifest = buildFileManifest(file);
    const rootFolder = manifest.relativePath.split("/")[0];

    if (!rootDocs[rootFolder]) {
      rootDocs[rootFolder] = createEmptyRootFolderDoc(rootFolder, websiteId);
    }

    addFileToTree(rootDocs[rootFolder], manifest);
  }

  return rootDocs;
}

export function getStarterProjectFiles(websiteId) {
  return [
    {
      relativePath: "src/main.jsx",
      content: `
        import React from "react";
        import ReactDOM from "react-dom/client";
        import App from "./App";
        import "./styles/globals.css";

        ReactDOM.createRoot(document.getElementById("root")).render(
          <React.StrictMode>
            <App />
          </React.StrictMode>
        );`,
      contentType: "text/plain",
    },
    {
      relativePath: "src/App.jsx",
      content: `
        import Home from "./pages/Home";

        export default function App() {
          return <Home />;
        }`,
      contentType: "text/plain",
    },
    {
      relativePath: "src/firebase/firebaseConfig.js",
      content: `
        import { initializeApp, getApps, getApp } from "firebase/app";
        import { getFirestore } from "firebase/firestore";

        const firebaseConfig = {
          apiKey: "AIzaSyBPHXPNOA2OI1ni4K7dZJUrU_6ePocUi0E",
          authDomain: "bakayise-website-builder.firebaseapp.com",
          projectId: "bakayise-website-builder",
          storageBucket: "bakayise-website-builder.firebasestorage.app",
          messagingSenderId: "452498662302",
          appId: "1:452498662302:web:13938ebc90efade8bfac8f",
          measurementId: "G-QCMG9TCRPM"
        };

        const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
        const db = getFirestore(app);

        export { app, db };
        export default firebaseConfig;`,
      contentType: "text/plain",
    },
    {
      relativePath: "src/pages/Home.jsx",
      content: `
        export default function Home() {
          return (
            <div className="min-h-screen bg-white text-slate-900">
              <main className="px-6 py-20">
                <section className="mx-auto max-w-5xl">
                  <h1 className="text-4xl font-bold tracking-tight">
                    Welcome to Kairos
                  </h1>
                  <p className="mt-4 text-lg text-slate-600">
                    Your AI-powered website and app builder.
                  </p>
                </section>
              </main>
            </div>
          );
        }`,
      contentType: "text/plain",
    },
    {
      relativePath: "src/components/_keep",
      content: `placeholder`,
      contentType: "text/plain",
    },
    {
      relativePath: "src/helpers/helpers.js",
      content: `
        export function classNames(...classes) {
          return classes.filter(Boolean).join(" ");
        }`,
      contentType: "text/plain",
    },
    {
      relativePath: "src/styles/globals.css",
      content: `
        @tailwind base;
        @tailwind components;
        @tailwind utilities;

        *,
        *::before,
        *::after {
          box-sizing: border-box;
        }

        * {
          margin: 0;
        }

        html {
          scrollbar-gutter: stable;
        }

        html:focus-within {
          scroll-behavior: smooth;
        }

        body {
          min-height: 100vh;
          text-rendering: optimizeLegibility;
          -webkit-font-smoothing: antialiased;
          line-height: 1.5;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            Helvetica, Arial, sans-serif;
          background-color: #ffffff;
          color: #0f172a;
        }

        img,
        picture,
        video,
        canvas,
        svg {
          display: block;
          max-width: 100%;
        }

        input,
        button,
        textarea,
        select {
          font: inherit;
        }

        textarea {
          overflow: auto;
        }

        p,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          overflow-wrap: break-word;
        }

        body {
          word-break: break-word;
        }

        #root {
          isolation: isolate;
          min-height: 100vh;
        }`,
      contentType: "text/css",
    },
    {
        relativePath: "assets/_keep",
        content: `placeholder`,
        contentType: "text/plain",
    },
    {
        relativePath: "public/_keep",
        content: `placeholder`,
        contentType: "text/plain",
    },
  ].map((file) => ({
    ...file,
    fullPath: getProjectFilePath(websiteId, file.relativePath),
  }));
}

export async function createProjectFileStructure({ websiteId, userId }) {
  const files = getStarterProjectFiles(websiteId);

  if (!Array.isArray(files)) {
    throw new Error("Starter files generation failed.");
  }

  for (const file of files) {
    const fileRef = ref(storage, file.fullPath);
    await uploadString(fileRef, file.content, "raw", {
      contentType: file.contentType,
    });
  }

  const rootFolderDocs = buildRootFolderDocuments(files, websiteId);
  const batch = writeBatch(db);

  for (const [rootFolderName, rootFolderDoc] of Object.entries(rootFolderDocs)) {
    const rootFolderRef = doc(
      db,
      "workspaces",
      userId,
      "websites",
      websiteId,
      "code",
      rootFolderName
    );

    batch.set(rootFolderRef, {
      ...rootFolderDoc,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  console.log("Root folder docs being written:", Object.keys(rootFolderDocs));
  console.log("Root folder docs full data:", rootFolderDocs);

  await batch.commit();

  return {
    success: true,
    websiteId,
    basePath: getProjectStorageBasePath(websiteId),
    filesCreated: files.map((f) => f.fullPath),
    manifestCreated: Object.keys(rootFolderDocs),
  };
}